/**
 * The game's design tokens as one pure table (ADR-061, amendment "the design system lands"). The client reads
 * it for the DOM and the canvas, and `scripts/ui-build.ts` emits it as the `--ot-` custom properties in
 * `packages/odyssey-client/src/ui/generated/tokens.css`. Values come from the design snapshot in
 * `docs/design/system`, corrected where the amendment says so. Sizes are whole logical pixels.
 */

/**
 * A solid colour as a six-digit lower-case hex.
 */
export type Hex = `#${string}`;

/**
 * Four per-side widths in logical pixels.
 */
export interface Sides {
  readonly left: number;
  readonly top: number;
  readonly right: number;
  readonly bottom: number;
}

/**
 * The master palette: the snapshot's raw ramps, darkest ink first as it delivers them, and `black`, the text
 * outline's own entry. Phaser's multiply tint leaves `#000000` black where it would shift ink 900 off the
 * palette, which is why the outline is not ink. Every UI pixel the build paints is one of these, a role
 * colour, transparent, or white where it is only ever drawn tinted.
 */
export const PALETTE = {
  "ink-900": "#07080b",
  "ink-800": "#0c0e13",
  "ink-700": "#12151c",
  "ink-600": "#191d26",
  "ink-500": "#222733",
  "ink-400": "#2e3442",
  "ink-300": "#434a5b",
  "vellum-100": "#f4ecd6",
  "vellum-200": "#e6dcc0",
  "vellum-300": "#b9ae94",
  "vellum-400": "#8a826f",
  "vellum-500": "#5f5a4f",
  "brass-100": "#f2d898",
  "brass-200": "#d6b36a",
  "brass-300": "#a8864a",
  "brass-400": "#735a31",
  "brass-500": "#46371f",
  "aether-200": "#a8fff4",
  "aether-300": "#5ff0e0",
  "aether-400": "#2fb8ac",
  "aether-500": "#1b6e68",
  black: "#000000",
} as const satisfies Readonly<Record<string, Hex>>;

/**
 * The name of a palette entry.
 */
export type PaletteName = keyof typeof PALETTE;

/**
 * Colour roles, in the snapshot's order, with the roles the game adds beyond it (`ADDED_ROLES`) beside their kin:
 * each names the palette entry it takes or holds a literal hex of its own. Roles that share a value (Protected and
 * Guarded, system chat and aether) are adopted as delivered and reassigned when their surfaces are designed; aether
 * means keyboard focus only.
 */
export const COLOUR_ROLES = {
  bg: "ink-800",
  surface: "ink-600",
  "surface-raised": "ink-500",
  "surface-sunken": "ink-700",
  "surface-hover": "ink-400",
  "frame-edge": "ink-900",
  "frame-metal": "brass-300",
  "frame-metal-dark": "brass-500",
  "frame-highlight": "brass-100",
  "frame-inner": "ink-900",
  text: "vellum-100",
  "text-muted": "vellum-300",
  "text-disabled": "vellum-500",
  "text-accent": "brass-100",
  "text-on-light": "ink-900",
  focus: "aether-300",
  "focus-dark": "aether-500",
  selected: "brass-200",
  "selected-fill": "#3a2f1c",
  fog: "#c9d3dc",
  "text-outline": "black",
  positive: "#7fd36a",
  warning: "#f0c040",
  danger: "#ff5a48",
  "danger-dark": "#7a1f18",
  info: "#7ab8ff",
  pending: "#c3b3f0",
  "flag-protected": "#9cc8f0",
  "flag-flagged": "#f0c040",
  "flag-combatant": "#ff5a48",
  "zone-lock": "#e6dcc0",
  "infamy-neutral": "vellum-100",
  "infamy-suspect": "#ff9a2e",
  "infamy-criminal": "#ff4a3a",
  "infamy-outlaw": "#b0122e",
  "zone-sanctuary": "#8fe0b8",
  "zone-guarded": "#8898c8",
  "zone-contested": "#f0c040",
  "zone-hostile": "#ff5a48",
  "zone-label": "vellum-200",
  health: "#e8463a",
  "health-dark": "#6e1a16",
  stamina: "#e8c24a",
  "stamina-dark": "#6a5418",
  "focus-res": "#3ab0d0",
  "focus-res-dark": "#12455a",
  accord: "#cdb6ff",
  "accord-dark": "#4a3a78",
  xp: "#b8e070",
  "xp-dark": "#3e5a1a",
  "bar-track": "ink-900",
  "bar-pending": "vellum-100",
  "accord-normal": "#8fe0c0",
  "accord-strained": "#f0c040",
  "accord-discordant": "#ff9a2e",
  "accord-dangerous": "#ff4a3a",
  "dmg-slashing": "#f4ecd6",
  "dmg-piercing": "#d4dde8",
  "dmg-blunt": "#e0b890",
  "dmg-thermal": "#ff8a3a",
  "dmg-kinetic": "#78d8ff",
  "dmg-resonant": "#e0a8ff",
  "dmg-void": "#a68cff",
  "dmg-poison": "#e040d0",
  heal: "#7fe070",
  crit: "#fff080",
  "rarity-common": "#d8d0bc",
  "rarity-uncommon": "#72d860",
  "rarity-rare": "#5aa8ff",
  "rarity-very-rare": "#c080ff",
  "rarity-legendary": "#ffa83a",
  "chat-local": "#f4ecd6",
  "chat-region": "#c8e090",
  "chat-global": "#f2d898",
  "chat-trade": "#f0a870",
  "chat-guild": "#70e8a8",
  "chat-party": "#80bcff",
  "chat-whisper": "#f09ae0",
  "chat-duel": "#ff7a68",
  "chat-system": "#d0d8e0",
  "party-1": "#ff4fd8",
  "party-2": "#9c6bff",
  "party-3": "#c0f030",
  "party-4": "#38d060",
  "party-5": "#b8783c",
  "party-6": "#20d0c0",
  "faction-lyceum": "#7aa0f0",
  "faction-underground": "#d06a4a",
  "faction-wanderers": "#98c868",
  "faction-remnant": "#c09ae8",
  "tier-novice": "#b9ae94",
  "tier-bronze": "#d08a50",
  "tier-silver": "#d4dae4",
  "tier-gold": "#f2c848",
  "tier-platinum": "#a0f0e4",
  "tier-diamond": "#98ccff",
  "tier-champion": "#ff8a6a",
} as const satisfies Readonly<Record<string, PaletteName | Hex>>;

/**
 * The name of a colour role.
 */
export type ColourRoleName = keyof typeof COLOUR_ROLES;

/**
 * Colour roles the game adds beyond the design snapshot (ADR-061's note of 2026-10-03, ADR-079's amendment for
 * P1.9-T66), each a stand-in until the designer delivers it, when it takes the designer's value and leaves this
 * list. `dmg-poison` is venom purple, poison damage's colour over another character. `party-1` to `party-6` are the
 * party members' own colours (ADR-061's note of 2026-10-14, P1.9-T180): magenta, violet, lime, green, brown and teal, drafts
 * for the author, none of them the colour of any other mark on the minimap.
 */
export const ADDED_ROLES = ["dmg-poison", "party-1", "party-2", "party-3", "party-4", "party-5", "party-6"] as const satisfies readonly ColourRoleName[];

/**
 * The modal scrim, the one translucent colour: a palette entry at a fixed opacity.
 */
export const SCRIM = { colour: "ink-900", alpha: 0.72 } as const satisfies { readonly colour: PaletteName; readonly alpha: number };

/**
 * The spacing steps in logical pixels.
 */
export const SPACING = [1, 2, 3, 4, 6, 8, 12, 16, 24] as const;

/**
 * The HUD's safe margin from the window's edges and the gutter between neighbouring HUD frames, in logical
 * pixels.
 */
export const HUD = { margin: 4, gutter: 2 } as const;

/**
 * One ring of a frame's border: a palette colour and its width on each side. A side of width 0 leaves that
 * edge to the next ring.
 */
export interface Ring {
  readonly colour: PaletteName;
  readonly widths: Sides;
}

/**
 * A nine-slice frame: its per-side insets (the `border-image-slice` and the `NineSlice` borders), the rings
 * that paint the border, the centre's fill and the transparent notch cut at each corner, in logical pixels.
 * Rings run outermost first. Each fills the band between the rectangle the rings before it leave and that
 * rectangle inset by its own widths, so a ring owns the corners where it has width on both sides, and the
 * widths on each side sum to that side's inset.
 */
export interface FrameTokens {
  readonly insets: Sides;
  readonly rings: readonly Ring[];
  readonly fill: PaletteName;
  readonly notch: 0 | 1 | 2;
}

/**
 * Equal widths on all four sides.
 *
 * @param width Width in logical pixels.
 * @returns The sides.
 */
function all(width: number): Sides {
  return { left: width, top: width, right: width, bottom: width };
}

/**
 * Widths on the given sides, zero elsewhere.
 *
 * @param widths The sides that have width.
 * @returns The sides.
 */
function only(widths: Partial<Sides>): Sides {
  return { left: widths.left ?? 0, top: widths.top ?? 0, right: widths.right ?? 0, bottom: widths.bottom ?? 0 };
}

/**
 * Every frame the export declares, in the amendment's order: its `--ot-9s-*` widths, the plate of the atlas
 * card, and the button with its per-side insets. The rings are the export's hard inset shadows read as
 * pixels: the window's top brass row is the highlight, the HUD's is metal over dark brass, the inset panel is
 * dark on the top and left and light on the right and bottom, and the button is one light row over its fill
 * and two dark rows beneath it. The bubble's only inner ring is its bottom shade; its fill colour pads the
 * other three sides to the declared inset of 2. Notches follow the export's `.ot-notch` (1) and `.ot-notch-2`
 * (2) classes. The bar is the export's `Bar` track: a one-pixel frame edge around the track colour, square, which
 * the bar track frame paints on (ADR-061's amendment "the game menu, character select, loading and confirm dialog
 * match the design").
 */
export const FRAMES = {
  window: {
    insets: all(4),
    rings: [
      { colour: "ink-900", widths: all(1) },
      { colour: "brass-100", widths: only({ top: 1 }) },
      { colour: "brass-300", widths: only({ left: 1, right: 1, bottom: 1 }) },
      { colour: "brass-500", widths: all(1) },
      { colour: "ink-900", widths: all(1) },
    ],
    fill: "ink-600",
    notch: 1,
  },
  dialog: {
    insets: all(5),
    rings: [
      { colour: "ink-900", widths: all(1) },
      { colour: "brass-100", widths: all(1) },
      { colour: "brass-300", widths: all(1) },
      { colour: "brass-500", widths: all(1) },
      { colour: "ink-900", widths: all(1) },
    ],
    fill: "ink-600",
    notch: 2,
  },
  hud: {
    insets: all(3),
    rings: [
      { colour: "ink-900", widths: all(1) },
      { colour: "brass-300", widths: only({ top: 1 }) },
      { colour: "brass-500", widths: only({ left: 1, right: 1, bottom: 1 }) },
      { colour: "ink-900", widths: all(1) },
    ],
    fill: "ink-600",
    notch: 1,
  },
  notice: {
    insets: all(3),
    rings: [
      { colour: "ink-900", widths: all(1) },
      { colour: "brass-300", widths: all(1) },
      { colour: "ink-900", widths: all(1) },
    ],
    fill: "ink-700",
    notch: 1,
  },
  panel: {
    insets: all(2),
    rings: [
      { colour: "ink-900", widths: only({ left: 1, top: 1 }) },
      { colour: "ink-400", widths: only({ right: 1, bottom: 1 }) },
      { colour: "ink-800", widths: all(1) },
    ],
    fill: "ink-700",
    notch: 0,
  },
  tooltip: {
    insets: all(2),
    rings: [
      { colour: "ink-900", widths: all(1) },
      { colour: "ink-300", widths: all(1) },
    ],
    fill: "ink-700",
    notch: 0,
  },
  field: {
    insets: all(2),
    rings: [
      { colour: "ink-900", widths: all(1) },
      { colour: "ink-400", widths: all(1) },
    ],
    fill: "ink-900",
    notch: 0,
  },
  slot: {
    insets: all(2),
    rings: [
      { colour: "ink-900", widths: all(1) },
      { colour: "ink-400", widths: all(1) },
    ],
    fill: "ink-800",
    notch: 0,
  },
  bubble: {
    insets: all(2),
    rings: [
      { colour: "ink-900", widths: all(1) },
      { colour: "vellum-300", widths: only({ bottom: 1 }) },
      { colour: "vellum-100", widths: only({ left: 1, top: 1, right: 1 }) },
    ],
    fill: "vellum-100",
    notch: 0,
  },
  plate: {
    insets: all(1),
    rings: [{ colour: "ink-900", widths: all(1) }],
    fill: "ink-900",
    notch: 1,
  },
  bar: {
    insets: all(1),
    rings: [{ colour: "ink-900", widths: all(1) }],
    fill: "ink-900",
    notch: 0,
  },
  button: {
    insets: only({ left: 1, top: 2, right: 1, bottom: 3 }),
    rings: [
      { colour: "ink-900", widths: all(1) },
      { colour: "ink-300", widths: only({ top: 1 }) },
      { colour: "ink-600", widths: only({ bottom: 2 }) },
    ],
    fill: "ink-400",
    notch: 0,
  },
} as const satisfies Readonly<Record<string, FrameTokens>>;

/**
 * The name of a frame the export declares.
 */
export type FrameName = keyof typeof FRAMES;

/**
 * The interactive states of rule 10 of the design brief, in the order a frame's name carries them
 * (ADR-061, amendment "the design system lands": `ui/<group>/<name>[_<state>][_<size>]`).
 */
export const FRAME_STATES = ["hover", "pressed", "focus", "disabled", "selected", "error"] as const;

/**
 * One ring of a variant: a palette entry or a colour role, and its width on each side.
 */
export interface VariantRing {
  readonly colour: PaletteName | ColourRoleName;
  readonly widths: Sides;
}

/**
 * A frame painted on the insets and notch of a frame the export declares, with rings and a fill of its
 * own, so a state or a variant never changes a control's size. Its rings follow `FrameTokens`' rules and
 * sum to the base frame's insets on every side.
 */
export interface FrameVariant {
  readonly base: FrameName;
  readonly rings: readonly VariantRing[];
  readonly fill: PaletteName | ColourRoleName;
}

/**
 * The frames the kit paints beyond the export's, named `<frame>[_<state>]` with states in `FRAME_STATES`
 * order: the button's primary and danger variants and the key glyph, all on the button's insets, and the
 * states that change a frame's pixels. The export draws its hover, danger and pressed looks with hexes off
 * the palette (`#5a6377`, `#e6c47a`, `#b32e24`); these are the nearest looks the palette and the roles
 * give. A disabled button of any variant uses `button_disabled`, except a secondary one that is selected, such as
 * the chosen segment of a choice that cannot be changed where it stands, which uses `button_disabled_selected`:
 * the selected fill under the disabled frame's dull rings, so the choice still reads as the choice. Keyboard focus on a button, a link or a
 * checkbox is drawn by the kit as a one-pixel outline in the focus colour, so it needs no frame; a focused
 * text field is `field_focus`, whose outer ring is the focus colour. A checkbox box is the field frame.
 *
 * A bar (ADR-061, "bars crop, never scale") is two frames: `bar-track`, on the bar's insets, the dark track in
 * the bar track colour inside its one-pixel frame edge, and `bar-fill`, on the field's insets, a progress fill strip whose top row is a highlight, whose bottom
 * row is a shade and whose rows between are one flat colour. A bar draws the fill across the whole inside
 * of its track and crops it to the value, so a fill of any width shows the same rows and edges.
 */
export const FRAME_VARIANTS = {
  button_hover: {
    base: "button",
    rings: [
      { colour: "ink-900", widths: all(1) },
      { colour: "vellum-500", widths: only({ top: 1 }) },
      { colour: "ink-600", widths: only({ bottom: 2 }) },
    ],
    fill: "ink-300",
  },
  button_pressed: {
    base: "button",
    rings: [
      { colour: "ink-900", widths: all(1) },
      { colour: "ink-600", widths: only({ top: 1 }) },
      { colour: "ink-400", widths: only({ bottom: 2 }) },
    ],
    fill: "ink-400",
  },
  button_disabled: {
    base: "button",
    rings: [
      { colour: "ink-900", widths: all(1) },
      { colour: "ink-500", widths: only({ top: 1 }) },
      { colour: "ink-700", widths: only({ bottom: 2 }) },
    ],
    fill: "ink-600",
  },
  button_selected: {
    base: "button",
    rings: [
      { colour: "ink-900", widths: all(1) },
      { colour: "brass-300", widths: only({ top: 1 }) },
      { colour: "brass-500", widths: only({ bottom: 2 }) },
    ],
    fill: "selected-fill",
  },
  button_disabled_selected: {
    base: "button",
    rings: [
      { colour: "ink-900", widths: all(1) },
      { colour: "brass-500", widths: only({ top: 1 }) },
      { colour: "ink-700", widths: only({ bottom: 2 }) },
    ],
    fill: "selected-fill",
  },
  "button-primary": {
    base: "button",
    rings: [
      { colour: "ink-900", widths: all(1) },
      { colour: "brass-100", widths: only({ top: 1 }) },
      { colour: "brass-400", widths: only({ bottom: 2 }) },
    ],
    fill: "brass-200",
  },
  "button-primary_hover": {
    base: "button",
    rings: [
      { colour: "ink-900", widths: all(1) },
      { colour: "vellum-100", widths: only({ top: 1 }) },
      { colour: "brass-400", widths: only({ bottom: 2 }) },
    ],
    fill: "brass-100",
  },
  "button-primary_pressed": {
    base: "button",
    rings: [
      { colour: "ink-900", widths: all(1) },
      { colour: "brass-400", widths: only({ top: 1 }) },
      { colour: "brass-200", widths: only({ bottom: 2 }) },
    ],
    fill: "brass-200",
  },
  "button-danger": {
    base: "button",
    rings: [
      { colour: "ink-900", widths: all(1) },
      { colour: "danger", widths: only({ top: 1 }) },
      { colour: "ink-800", widths: only({ bottom: 2 }) },
    ],
    fill: "danger-dark",
  },
  "button-danger_hover": {
    base: "button",
    rings: [
      { colour: "ink-900", widths: all(1) },
      { colour: "vellum-100", widths: only({ top: 1 }) },
      { colour: "danger-dark", widths: only({ bottom: 2 }) },
    ],
    fill: "danger",
  },
  "button-danger_pressed": {
    base: "button",
    rings: [
      { colour: "ink-900", widths: all(1) },
      { colour: "ink-800", widths: only({ top: 1 }) },
      { colour: "danger-dark", widths: only({ bottom: 2 }) },
    ],
    fill: "danger-dark",
  },
  key: {
    base: "button",
    rings: [
      { colour: "ink-900", widths: all(1) },
      { colour: "vellum-100", widths: only({ top: 1 }) },
      { colour: "vellum-400", widths: only({ bottom: 2 }) },
    ],
    fill: "vellum-100",
  },
  field_hover: {
    base: "field",
    rings: [
      { colour: "ink-900", widths: all(1) },
      { colour: "ink-300", widths: all(1) },
    ],
    fill: "ink-900",
  },
  field_pressed: {
    base: "field",
    rings: [
      { colour: "ink-900", widths: all(1) },
      { colour: "ink-800", widths: all(1) },
    ],
    fill: "ink-800",
  },
  field_focus: {
    base: "field",
    rings: [
      { colour: "focus", widths: all(1) },
      { colour: "focus-dark", widths: all(1) },
    ],
    fill: "ink-700",
  },
  field_disabled: {
    base: "field",
    rings: [
      { colour: "ink-900", widths: all(1) },
      { colour: "ink-700", widths: all(1) },
    ],
    fill: "ink-700",
  },
  field_error: {
    base: "field",
    rings: [
      { colour: "danger", widths: all(1) },
      { colour: "danger-dark", widths: all(1) },
    ],
    fill: "ink-900",
  },
  field_focus_error: {
    base: "field",
    rings: [
      { colour: "focus", widths: all(1) },
      { colour: "danger", widths: all(1) },
    ],
    fill: "ink-700",
  },
  "bar-track": {
    base: "bar",
    rings: [{ colour: "frame-edge", widths: all(1) }],
    fill: "bar-track",
  },
  "bar-fill": {
    base: "field",
    rings: [
      { colour: "vellum-100", widths: only({ top: 1 }) },
      { colour: "vellum-500", widths: only({ bottom: 1 }) },
      { colour: "vellum-200", widths: only({ left: 2, top: 1, right: 2, bottom: 1 }) },
    ],
    fill: "vellum-200",
  },
} as const satisfies Readonly<Record<string, FrameVariant>>;

/**
 * The name of a frame variant.
 */
export type FrameVariantName = keyof typeof FRAME_VARIANTS;

/**
 * Frames painted from a pixel grid rather than a ring stack: the art is the grid `art/frames/<name>.grid` under
 * the client's UI art, and the table holds each frame's per-side insets, which slice it. The interaction prompt's
 * corner bracket (P1.9-T17) is one: its four corners hold the whole drawing, and its edges and centre are clear,
 * so it marks a box of any size.
 */
export const GRID_FRAMES = {
  bracket: { insets: all(5) },
} as const satisfies Readonly<Record<string, { readonly insets: Sides }>>;

/**
 * The name of a frame painted from a grid.
 */
export type GridFrameName = keyof typeof GRID_FRAMES;

/**
 * Icon sizes by role in logical pixels. A role takes the native size of the Pixelarticons set that fills it;
 * the free 24 set is the only one present, so every role is 24.
 */
export const ICON_SIZES = {
  slot: 24,
  list: 24,
  button: 24,
  field: 24,
  nameplate: 24,
  "menu-item": 24,
} as const;

/**
 * Noto Sans roles for running text in the DOM: size and line height in logical pixels. Pixel-font roles are
 * multiples of the pixel face's measured native size (`PIXEL_TYPE`).
 */
export const BODY_TYPE = {
  body: { size: 7, lineHeight: 10 },
  "body-sm": { size: 6, lineHeight: 9 },
  caption: { size: 6, lineHeight: 8 },
  field: { size: 7, lineHeight: 10 },
} as const;

/**
 * A pixel face's metrics in font pixels: pixels to the em (its native size, at which one font pixel is one
 * logical pixel), how many rows `H` rises above the baseline, the line's height, and the baseline measured
 * down from the line's top.
 */
export interface PixelFaceMetrics {
  readonly nativeSize: number;
  readonly capHeight: number;
  readonly lineHeight: number;
  readonly baseline: number;
}

/**
 * The pixel faces as the font build measures them on their glyph grids (ADR-061, amendment "the design
 * system lands", "pixel fonts come from a measured glyph grid"): `pixel` is Pixelify Sans 400, the only font
 * on the canvas and the DOM's labels, and `title` is Jacquard 24, for menu titles only. `bun run ui:build`
 * fails when a measurement and this table disagree, so the values here are the measurement, not the export's
 * assumed 10 and 12.
 */
export const PIXEL_FACES = {
  pixel: { nativeSize: 11, capHeight: 7, lineHeight: 13, baseline: 10 },
  title: { nativeSize: 43, capHeight: 25, lineHeight: 43, baseline: 34 },
} as const satisfies Readonly<Record<string, PixelFaceMetrics>>;

/**
 * The small pixel face's metrics (ADR-061's note of 2026-10-16, P1.9-T208): original kit art on a 4 by 6 cell, drawn
 * as whole pixels rather than sampled from a font, for text under the pixel face's 11 pixel size. Its native size is its
 * line (9 pixels: the capitals' 6 rows, one descender row and the one pixel outline above and below the ink), so one font
 * pixel is one logical pixel at `font-size: 9`. `bun run ui:build` fails when the drawn glyphs disagree with this
 * table.
 */
export const PIXEL_SMALL_FACE = { nativeSize: 9, capHeight: 6, lineHeight: 9, baseline: 7 } as const satisfies PixelFaceMetrics;

/**
 * The name of a pixel face.
 */
export type PixelFaceName = keyof typeof PIXEL_FACES;

/**
 * Pixel type roles as whole multiples of the pixel face's native size: 1x for label, button, number and
 * nameplate, 2x for panel title and large number, 3x for display. A pixel face appears at no other size.
 */
export const PIXEL_TYPE = {
  label: 1,
  button: 1,
  number: 1,
  nameplate: 1,
  "panel-title": 2,
  "number-lg": 2,
  display: 3,
} as const;

/**
 * The name of a pixel type role.
 */
export type PixelTypeRole = keyof typeof PIXEL_TYPE;

/**
 * Colour roles kept as visual values only until the author sets them (ADR-061, amendment "the export is a
 * visual reference"). None is open: the Bible gives Suspect an orange name and Criminal a red one, and the
 * author set the Outlaw's, a deep crimson, darker than the Criminal's red, that stays legible inside the pixel font's black outline.
 */
export const PROPOSED_ROLES = [] as const satisfies readonly (keyof typeof COLOUR_ROLES)[];

/**
 * One UI frame in milliseconds (25 frames a second). Every UI duration is a whole number of these.
 */
export const FRAME_MS = 40;

/**
 * UI durations in whole frames. The DOM steps with CSS `steps(n)` and the canvas counts on the scene clock.
 */
export const MOTION_FRAMES = {
  press: 1,
  open: 3,
  close: 2,
  flash: 2,
  "toast-in": 3,
  "toast-hold": 100,
  "toast-out": 6,
  message: 100,
  banner: 75,
  blink: 12,
  "busy-dot": 6,
  "bar-trail-hold": 6,
  float: 20,
  "float-crit": 25,
  "bubble-min": 100,
  "bubble-max": 250,
  "idle-loop": 48,
  "spinner-step": 2,
} as const;

/**
 * The name of a UI duration.
 */
export type MotionName = keyof typeof MOTION_FRAMES;

/**
 * The most flashes any UI element may show in one second.
 */
export const FLASH_MAX_HZ = 3;

/**
 * Resolves a palette entry or a colour role to its hex.
 *
 * @param name A palette entry or a role name.
 * @returns The six-digit hex the name stands for.
 */
export function resolveColour(name: PaletteName | ColourRoleName): Hex {
  if (name in PALETTE) {
    return PALETTE[name as PaletteName];
  }
  const value: PaletteName | Hex = COLOUR_ROLES[name as ColourRoleName];
  return value.startsWith("#") ? (value as Hex) : PALETTE[value as PaletteName];
}

/**
 * A pixel type role's size, line height and baseline in logical pixels: the pixel face's native size, line
 * height and baseline, each times the role's multiple.
 *
 * @param role The role.
 * @returns Its size, line height and baseline.
 * @example
 * pixelType("panel-title");
 * // { size: 22, lineHeight: 26, baseline: 20 }
 */
export function pixelType(role: PixelTypeRole): { size: number; lineHeight: number; baseline: number } {
  const face = PIXEL_FACES.pixel;
  const multiple = PIXEL_TYPE[role];
  return { size: face.nativeSize * multiple, lineHeight: face.lineHeight * multiple, baseline: face.baseline * multiple };
}

/**
 * A UI duration in milliseconds.
 *
 * @param name The duration's name.
 * @returns Its frames times the frame length.
 */
export function durationMs(name: MotionName): number {
  return MOTION_FRAMES[name] * FRAME_MS;
}
